import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Select as AntdSelect,
  Badge,
  Button,
  Flex,
  Space,
  Tooltip
} from 'antd';
import moment from 'moment';
import {
  ArrowClockwise,
  ArrowSquareOut,
  FileArrowUp,
  FileDotted,
  Plus
} from 'phosphor-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../AppContext';
import {
  FULL_DATE_TIME_FORMAT,
  LIST_TYPES,
  MODULES,
  ORDER,
  PAGES_SORT_FIELD,
  PAGE_TYPE_LABELS,
  ROUTES,
  STATUS_TYPES,
  TIME_PERIOD,
  WEB_ROUTES,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION
} from '../../common/constants';
import { modalContext } from '../../components/AppComponentContainer';
import FilterBar, { useFilterBar } from '../../components/FilterBar';
import PageHeader from '../../components/PageHeader';
import PageList from '../../components/PageList';
import useCheckPermission from '../../hooks/useCheckPermission';
import AddEditGroupModal from './component/groups/AddEditGroupModal';
import SelectGroup from './component/groups/SelectGroup';
import PreviewModal from './component/pageModules/Preview/PreviewModal';
import {
  DELETE_PAGE,
  DELETE_PAGE_GROUP,
  FORCE_PURGE_PAGE_CACHE,
  UPDATE_PAGE_STATUS
} from './graphql/Mutations';
import { GET_PAGES, GET_WORKSPACE_APP } from './graphql/Queries';

const variablesSelector = ({
  limit,
  offset,
  search,
  sortField,
  sortOrder,
  isDefault,
  pageGroupId
}) => ({
  filter: {
    limit,
    skip: offset,
    search,
    isDefault,
    pageGroupId
  },
  sort: {
    sortOn: sortField,
    sortBy: sortOrder
  }
});

const dataSelector = ({ pagesAdmin }) => ({
  data: pagesAdmin?.pages || [],
  count: pagesAdmin?.count || 0
});

const PAGE_TYPE_OPTIONS = [
  { label: 'Custom', value: false },
  { label: 'Default', value: true }
];

const Action = ({
  status,
  id,
  refresh,
  removeItem,
  isDefault,
  allowDelete,
  slug,
  type,
  isDeleteAllowed,
  isAddEditAllowed,
  setPageId,
  webUrl
}) => {
  const [disablePurgeBtn, setDisablePurgeBtn] = useState(false);
  const [updatePageStatus] = useMutation(UPDATE_PAGE_STATUS);
  const [deletePage] = useMutation(DELETE_PAGE);
  const [purgePageCache, { loading: isPurging }] = useMutation(
    FORCE_PURGE_PAGE_CACHE
  );
  const newStatus =
    status === STATUS_TYPES.DRAFT ? STATUS_TYPES.PUBLISHED : STATUS_TYPES.DRAFT;

  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.PAGE_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const title = {
    [STATUS_TYPES.PUBLISHED]: 'Publish this page',
    [STATUS_TYPES.DRAFT]: 'Draft this page'
  }[newStatus];

  const showPurgeTitle = (pageStatus) => {
    if (pageStatus === STATUS_TYPES.DRAFT) {
      return 'Only published page can be purged';
    }
    if (pageStatus === STATUS_TYPES.PUBLISHED && disablePurgeBtn) {
      return 'Purging page cache';
    }
    return 'Force purge page cache';
  };
  const Icon = {
    [STATUS_TYPES.PUBLISHED]: FileArrowUp,
    [STATUS_TYPES.DRAFT]: FileDotted
  }[newStatus];

  const isDeleteDisable =
    (isDefault || !allowDelete) && status === STATUS_TYPES.PUBLISHED;

  const handleDelete = () => {
    if (!isDeleteDisable)
      modalContext?.confirm({
        title: 'Are you sure, you want to delete this page?',
        centered: true,
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          deletePage({
            variables: { id }
          })
            .then(({ errors }) => {
              if (removeItem && !errors) {
                removeItem(id);
              }
            })
            .catch();
        }
      });
  };

  const handleShowPreview = (e, pageId) => {
    e.preventDefault();
    setPageId(pageId);
  };

  const handleUpdateStatus = (modalTitle) => {
    modalContext?.confirm({
      title: modalTitle,
      centered: true,
      okText: 'Yes',
      cancelText: 'No',
      okType: 'primary',

      onOk() {
        updatePageStatus({
          variables: { id, status: newStatus }
        })
          .then(({ errors }) => {
            if (refresh && !errors) {
              refresh();
            }
          })
          .catch();
      }
    });
  };

  const forcePurge = () => {
    setDisablePurgeBtn(true);

    if (slug && type) {
      purgePageCache({
        variables: {
          slug,
          type
        }
      });
    }
    setTimeout(() => {
      setDisablePurgeBtn(false);
    }, TIME_PERIOD.ONE_MINUTE);
  };

  const url = `${webUrl}${WEB_ROUTES[type] ?? ''}/${slug?.toLowerCase()}`;

  const renderTitle = () => {
    if (status === STATUS_TYPES.PUBLISHED) return 'Draft this page to edit';
    return 'Edit page';
  };

  return (
    <div className="d-flex align-center">
      {isAddEditAllowed && (
        <>
          {!isDefault && (
            <Tooltip title="Open in web">
              <Button
                className="text-btn d-flex align-center justify-center"
                type="text"
                icon={<ArrowSquareOut size={22} />}
                disabled={status !== STATUS_TYPES.PUBLISHED}
                // eslint-disable-next-line no-undef
                onClick={() => window?.open(url)}
              />
            </Tooltip>
          )}
          <Tooltip title={title}>
            <Button
              className="text-btn d-flex align-center justify-center"
              type="text"
              onClick={() => {
                handleUpdateStatus(
                  `Are you sure, you want to ${title?.toLowerCase()}?`
                );
              }}
            >
              <Icon size={22} />
            </Button>
          </Tooltip>
          <Tooltip title={showPurgeTitle(status)}>
            <Button
              className="text-btn d-flex align-center justify-center"
              type="text"
              icon={<ArrowClockwise size={22} />}
              onClick={forcePurge}
              loading={isPurging}
              disabled={disablePurgeBtn || status === STATUS_TYPES.DRAFT}
            />
          </Tooltip>
        </>
      )}
      <Tooltip title="Preview">
        <Button
          className="text-btn d-flex align-center justify-center"
          type="text"
          onClick={(e) => {
            handleShowPreview(e, id);
          }}
        >
          <EyeOutlined size={20} />
        </Button>
      </Tooltip>
      {!isViewOnly && (
        <Tooltip title={renderTitle()}>
          <Link
            to={`${ROUTES?.PAGES_MODULE}/${id}/edit`}
            disabled={status === STATUS_TYPES.PUBLISHED}
            className="text-btn black-btn"
            type="text"
            onClick={(e) => {
              if (status === STATUS_TYPES.PUBLISHED) {
                e.preventDefault();
              }
            }}
          >
            <EditOutlined />
          </Link>
        </Tooltip>
      )}

      {isDeleteAllowed && (
        <Tooltip
          title={
            isDeleteDisable ? 'You can not delete this page' : 'Delete page'
          }
        >
          <Button
            disabled={isDeleteDisable}
            className="text-btn"
            type="text"
            onClick={handleDelete}
          >
            <DeleteOutlined />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

export const Status = ({ status }) => {
  return (
    <div>
      {status === STATUS_TYPES.DRAFT && (
        <Badge className="status" count={status} color="#5E5957" />
      )}
      {status === STATUS_TYPES.PUBLISHED && (
        <Badge className="status" count={status} color="#52c41a" />
      )}
      {status === STATUS_TYPES.UNPUBLISHED && (
        <Badge className="status" count={status} />
      )}
    </div>
  );
};

const tableColumns = (
  { refresh, removeItem },
  isDefault,
  isAddEditAllowed,
  isDeleteAllowed,
  setPageId,
  webUrl
) => [
  {
    title: 'Name',
    dataIndex: 'title',
    key: 'title',
    width: 150
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    render: (text, record) => (
      <Space>
        <span>{PAGE_TYPE_LABELS[text]}</span>
        <span>
          <b>{record?.isDefault ? 'Default' : null}</b>
        </span>
      </Space>
    ),
    width: 100
  },
  ...(!isDefault
    ? [
        {
          title: 'Slug',
          dataIndex: 'slug',
          key: 'slug'
        }
      ]
    : []),
  {
    title: 'Page Group',
    dataIndex: ['pageGroup', 'name'],
    key: 'pageGroup',
    width: 140,
    render: (value) => value ?? '-'
  },
  {
    title: 'Last modified',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: (_, record) => (
      <span>{moment(record?.updatedAt).format(FULL_DATE_TIME_FORMAT)}</span>
    )
  },

  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (text, record) => <Status status={text} {...record} />,
    width: 100
  },
  {
    title: 'Actions',
    dataIndex: 'id',
    render: (_, record) => (
      <Action
        {...record}
        refresh={refresh}
        removeItem={removeItem}
        isDeleteAllowed={isDeleteAllowed}
        isAddEditAllowed={isAddEditAllowed}
        setPageId={setPageId}
        webUrl={webUrl}
      />
    )
  }
];

const SelectPageGroup = ({ value, onChange }) => {
  const ref = useRef(null);
  const [modalState, setModalState] = useState(null);

  const handleSuccess = () => {
    if (ref.current?.refetch) {
      ref.current.refetch();
    }
  };

  const [deletePageGroup] = useMutation(DELETE_PAGE_GROUP, {
    onCompleted() {
      handleSuccess();
    },
    onError() {}
  });

  const handleClose = () => {
    setModalState({
      open: false,
      data: null
    });
  };

  const handleAddGroup = () => {
    setModalState({
      open: true,
      data: ''
    });
  };

  const handleDelete = (id) => {
    if (!id) return;
    modalContext?.confirm({
      title: 'Are you sure, you want to delete this page group?',
      centered: true,
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        deletePageGroup({
          variables: {
            where: { id }
          }
        });
      }
    });
  };

  return (
    <>
      <AddEditGroupModal
        {...modalState}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
      <SelectGroup
        ref={ref}
        value={value}
        onChange={onChange}
        dropdownRender={(menu) => {
          return (
            <div>
              <Button
                className="select-add-btn"
                type="text"
                icon={<Plus size={22} />}
                onClick={handleAddGroup}
                onMouseDown={(e) => e.stopPropagation()}
              >
                Create group
              </Button>
              {menu}
            </div>
          );
        }}
        optionRender={(option) => (
          <Flex
            className="group-select-option"
            align="center"
            justify="space-between"
            gap={10}
          >
            <span className="mr-16">{option?.label}</span>
            <Flex align="center">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setModalState({
                    open: true,
                    data: {
                      name: option?.label ?? '',
                      id: option?.value ?? ''
                    }
                  });
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
              />
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(option?.value);
                }}
              />
            </Flex>
          </Flex>
        )}
      />
    </>
  );
};

function Pages({ history }) {
  const { state } = useApp();
  const [group, setGroup] = useState(null);
  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.PAGE_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isDeleteAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.PAGE_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.DELETE]
    }
  ]);

  const [isDefault, setIsDefault] = useState(false);
  const [pageId, setPageId] = useState(null);
  const [disablePurgeAllBtn, setDisablePurgeAllBtn] = useState(false);
  const [purgeAllPageCache, { loading: isPurging }] = useMutation(
    FORCE_PURGE_PAGE_CACHE
  );

  const { data } = useQuery(GET_WORKSPACE_APP, {
    fetchPolicy: 'network-only',
    variables: {
      where: {
        id: state?.app?.id
      }
    }
  });

  const webUrl = data
    ? data?.workspaceApp?.url || process.env.REACT_APP_WEB_URL
    : '';

  const columns = useCallback(
    (props) =>
      tableColumns(
        props,
        isDefault,
        isAddEditAllowed,
        isDeleteAllowed,
        setPageId,
        webUrl
      )?.filter((item) => item !== false),
    [isDefault, webUrl]
  );

  const filterProps = useFilterBar({
    defaultSortOptions: {
      options: PAGES_SORT_FIELD,
      order: ORDER[1].value,
      field: PAGES_SORT_FIELD[0].value
    }
  });

  const filters = useMemo(
    () => ({
      ...filterProps.filters,
      isDefault,
      pageGroupId: group?.value ?? null
    }),
    [filterProps.filters, isDefault, group]
  );

  const handleShowModal = () => {
    history?.push(`${ROUTES?.PAGES_MODULE}/add`, { isUpdate: false });
  };

  const handleIsDefaultChange = (val) => setIsDefault(val);

  const forcePurge = () => {
    setDisablePurgeAllBtn(true);
    purgeAllPageCache({
      variables: {
        all: true
      }
    });
    setTimeout(() => {
      setDisablePurgeAllBtn(false);
    }, TIME_PERIOD.ONE_MINUTE);
  };

  const handleCancel = () => {
    setPageId(null);
  };

  const handleGroupChange = (_, value) => setGroup(value);

  return (
    <>
      <PreviewModal open={pageId} handleCancel={handleCancel} pageId={pageId} />
      <PageHeader
        menu={MODULES?.UI_CONFIGURATION}
        handleShowModal={handleShowModal}
        isAddEditAllowed={isAddEditAllowed}
      />
      <FilterBar
        {...filterProps}
        SearchProps={{ placeholder: 'Search Pages' }}
        show={{ listModes: false }}
      >
        <SelectPageGroup value={group} onChange={handleGroupChange} />
        <AntdSelect
          value={isDefault}
          options={PAGE_TYPE_OPTIONS}
          onChange={handleIsDefaultChange}
        />
        <Tooltip
          title={
            disablePurgeAllBtn
              ? 'purging page cache..'
              : 'Force purge all page cache'
          }
          placement="left"
        >
          <Button
            className="text-btn d-flex align-center justify-center"
            type="text"
            icon={<ArrowClockwise size={24} />}
            onClick={forcePurge}
            loading={isPurging}
            disabled={disablePurgeAllBtn}
          />
        </Tooltip>
      </FilterBar>
      <PageList
        filters={filters}
        listMode={LIST_TYPES.TABLE}
        query={GET_PAGES}
        variablesSelector={variablesSelector}
        dataSelector={dataSelector}
        TableProps={{ columns }}
        limit={30}
      />
    </>
  );
}

export default Pages;
